import React from 'react';
import { motion } from 'framer-motion';
import { ChartBarIcon, ClockIcon, PresentationChartLineIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';

const FEATURE_TITLE = "Complexity Assessment";
const FEATURE_DESCRIPTION = "Infection complexity scoring (0-10) with visual representation and classification to guide clinical decision-making.";

const ComplexityAssessmentPage: React.FC = () => {
  return (
    <div className="py-16 bg-background">
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1 
            className="text-4xl font-extrabold text-text tracking-tight sm:text-5xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {FEATURE_TITLE}
          </motion.h1>
          <motion.p 
            className="mt-4 text-xl text-text max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {FEATURE_DESCRIPTION}
          </motion.p>
        </div>

        <div className="mt-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <ChartBarIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Sophisticated Scoring</h3>
              <p className="text-text">
                Our proprietary 0-10 complexity scoring system provides a clear measure of infection 
                complexity, helping clinicians quickly assess the severity and required management.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Nuanced 0-10 scoring scale for detailed assessment</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Multi-factorial algorithm considers 15+ variables</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Regularly calibrated based on clinical outcomes</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <PresentationChartLineIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Visual Representation</h3>
              <p className="text-text">
                We present complexity data through intuitive visual representations including charts, 
                color-coding, and comparative metrics to aid rapid clinical assessment.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Color-coded severity indicators</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Interactive complexity breakdowns</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Trending data to show progress over time</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <ClockIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Decision Support</h3>
              <p className="text-text">
                Our complexity classifications provide actionable guidance for clinical 
                decision-making, helping optimize resource allocation and care pathways.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Care setting recommendations</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Specialist consultation guidance</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Resource allocation suggestions</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>

        <motion.div 
          className="mt-16 bg-section-light p-8 rounded-lg shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-text mb-4">Complexity Classification Guide</h2>
          <div className="overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-text mb-4">Complexity Scoring Reference</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div className="bg-score-none h-4 rounded-full" style={{ width: '20%' }}></div>
                    </div>
                    <div className="ml-4 w-24 flex-shrink-0">
                      <p className="font-medium">0-2: Mild</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div className="bg-score-low h-4 rounded-full" style={{ width: '40%' }}></div>
                    </div>
                    <div className="ml-4 w-24 flex-shrink-0">
                      <p className="font-medium">3-4: Moderate</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div className="bg-score-moderate h-4 rounded-full" style={{ width: '60%' }}></div>
                    </div>
                    <div className="ml-4 w-24 flex-shrink-0">
                      <p className="font-medium">5-6: Significant</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div className="bg-score-high h-4 rounded-full" style={{ width: '80%' }}></div>
                    </div>
                    <div className="ml-4 w-24 flex-shrink-0">
                      <p className="font-medium">7-8: Severe</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div className="bg-score-critical h-4 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                    <div className="ml-4 w-24 flex-shrink-0">
                      <p className="font-medium">9-10: Critical</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-text mb-4">Clinical Implications</h3>
                <div className="space-y-3 text-sm">
                  <div className="p-3 bg-white border border-border rounded">
                    <p className="font-medium text-score-none">0-2: Mild</p>
                    <p>Outpatient management, routine follow-up, standard treatment protocols.</p>
                  </div>
                  <div className="p-3 bg-white border border-border rounded">
                    <p className="font-medium text-score-low">3-4: Moderate</p>
                    <p>Outpatient management with close monitoring, consider extended therapy.</p>
                  </div>
                  <div className="p-3 bg-white border border-border rounded">
                    <p className="font-medium text-score-moderate">5-6: Significant</p>
                    <p>Consider short hospitalization, specialist consult recommended, enhanced monitoring.</p>
                  </div>
                  <div className="p-3 bg-white border border-border rounded">
                    <p className="font-medium text-score-high">7-8: Severe</p>
                    <p>Inpatient management required, infectious disease consult, consider IV therapy.</p>
                  </div>
                  <div className="p-3 bg-white border border-border rounded">
                    <p className="font-medium text-score-critical">9-10: Critical</p>
                    <p>Immediate hospitalization, possible ICU admission, aggressive therapeutic approach.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-16 text-center">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              href="/contact"
              variant="primary"
              size="lg"
            >
              Schedule a Demo
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ComplexityAssessmentPage; 